import { Injectable, OutputRef, Type, inject, reflectComponentType } from '@angular/core';
import { NgbModal, NgbModalOptions, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SbModalHostComponent } from './sb-modal-host.component';
import { inputGiver, modalComponentRef } from './sb-modal-inputs';
import { SbComponentModalRef, SbModalInputs, SbModalOptions, SbModalOutputs, SbModalSize } from './sb-modal.types';

@Injectable({ providedIn: 'root' })
export class SbModalService {
  private readonly modal = inject(NgbModal);

  /**
   * Opens a component as the whole modal, as NgbModal.open does: its own chrome and options, its
   * NgbActiveModal, `.result` and `.componentInstance`. The component is given `inputs` as a parent's
   * bindings would (through ComponentRef.setInput, since a signal input cannot be assigned), before
   * its first change detection, so ngOnInit already sees them. `outputs` are listened to as a parent's
   * `(output)` bindings would, from the start and until the modal's component is destroyed.
   */
  openComponent<T>(
    component: Type<T>,
    options: NgbModalOptions = {},
    inputs: SbModalInputs<T> = {},
    outputs: SbModalOutputs<T> = {},
  ): SbComponentModalRef<T> {
    // Refused before opening, so a wrong key leaves no dialog behind.
    const declared = (reflectComponentType(component)?.outputs || []).map(({propName}) => propName);
    const notAnOutput = Object.keys(outputs).find((key) => !declared.includes(key));
    if (notAnOutput) {
      throw new Error(`${notAnOutput} is not an output: it cannot be listened to.`);
    }
    const ref = this.modal.open(component, options) as SbComponentModalRef<T>;
    const give = inputGiver(modalComponentRef<T>(ref));
    Object.entries(inputs).forEach(([key, value]) => give(key, value));
    Object.entries(outputs).forEach(([key, listener]) => {
      const output = (ref.componentInstance as Record<string, unknown>)[key] as OutputRef<unknown>;
      output.subscribe(listener as (value: unknown) => void);
    });
    return ref;
  }

  open(options: SbModalOptions = {}): NgbModalRef {
    const ngbOptions: NgbModalOptions = {
      size: this.mapSize(options.size, options.variant),
      centered: options.centered ?? true,
      scrollable: options.scrollable ?? true,
      keyboard: options.keyboard ?? true,
      backdrop: options.backdrop ?? true,
      windowClass: [
        options.windowClass || '',
        options.variant === 'fullscreen' ? 'sb-modal-window--fullscreen' : '',
        // Every inline entity add/edit popup gets the same chrome fixes as the ones opened
        // through EntityFormModalService (no card-in-a-card, no duplicated header, sticky
        // actions row) — they are the same thing, only opened a different way.
        options.variant === 'form' ? 'sb-entity-form-modal' : '',
        // Deliberately NOT `snoball-iframe-modal-window`: that class is for the editor/browser
        // iframes, which supply their own chrome, so it strips the modal surface entirely
        // (transparent content, no border, no shadow) and pins a 360px body.
        options.variant === 'iframe' ? 'sb-modal-window--iframe' : '',
      ]
        .filter(Boolean)
        .join(' '),
    };

    return this.openComponent(SbModalHostComponent, ngbOptions, {options});
  }

  confirm(title: string, body: string, confirmLabel = 'Confirm'): Promise<boolean> {
    return this.open({
      title,
      body,
      variant: 'confirm',
      confirmLabel,
      size: 'md',
    })
      .result.then(() => true)
      .catch(() => false);
  }

  private mapSize(
    size: SbModalSize | undefined,
    variant: SbModalOptions['variant'],
  ): 'sm' | 'lg' | 'xl' | undefined {
    if (variant === 'iframe' || variant === 'fullscreen') {
      return size === 'sm' ? 'sm' : size === 'lg' ? 'lg' : 'xl';
    }
    if (!size || size === 'md') {
      return undefined;
    }
    return size;
  }
}
